import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography, TextField } from '@mui/material';
import TextFieldInput from '../../field-array/TextField';
import Select from '../../field-array/Select';
import useReactHookFormStyles from '../../../../../custom-components/form-components/ReactHookForm.styles';

const ContainerSx = {
    border: (th) => `1px solid ${th.palette.divider}`,
    borderRadius: '4px',
    padding: 2,
};

const RemoveButtonSx = {
    display: 'flex',
    justifyContent: 'flex-end',
};

function PumpDurations({ name, usePumpSpeed, durations, props: { label, buttonLabel, required } }) {
    const { control, getValues, watch } = useFormContext();
    const { fieldArray } = useReactHookFormStyles();

    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    const getStartWeight = (index) => {
        const previousEnd = getValues(`${name}[${index - 1}].maxWeight`);
        if (index === 0 || !previousEnd) return 0;
        return previousEnd + 1;
    };

    const validateMaxWeight = () => {
        const values = getValues(name);
        return values?.every((field, index) => {
            if (index === 0) return field.maxWeight > 0;
            const previousMax = values[index - 1]?.maxWeight ?? 0;
            return field.maxWeight > previousMax;
        });
    };

    return (
        <Grid sx={ContainerSx}>
            <Typography
                sx={{ mb: 1 }}
                variant="body2"
                fontWeight="bold"
                // wrap
            >
                {`${label}${required ? '*' : ''}`}
            </Typography>
            {fields.map((field, index) => (
                <Grid key={`${field.name}.${field.id}`} container className={fieldArray} spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {usePumpSpeed ? (
                                <Grid item xs={2}>
                                    <TextFieldInput key={`pump_speed.${field.id}`} name={`${name}.${index}.pump_speed`} fieldName={name} variant="outlined" label="Pump Speed (lbs/min)" InputLabelProps={{ shrink: true }} type="number" InputProps={{ inputProps: { min: 0, step: 1 } }} valueAsNumber {...{ control, index, field }} />
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={2} sx={{ mt: 2 }}>
                                        <TextField variant="outlined" label="Start Weight (lb)" InputLabelProps={{ shrink: true }} type="number" InputProps={{ inputProps: { min: 0, step: 1 } }} value={getStartWeight(index)} disabled />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <TextFieldInput key={`end.${field.id}`} name={`${name}.${index}.maxWeight`} fieldName={name} variant="outlined" label="End Weight (lb)" InputLabelProps={{ shrink: true }} type="number" InputProps={{ inputProps: { min: 0, step: 1 } }} valueAsNumber {...{ control, index, field }} />
                                    </Grid>
                                    <Grid item xs={2} sx={{ mt: 1 }}>
                                        <Select
                                            key={`duration.${field.id}`}
                                            name={`${name}.${index}.duration`}
                                            props={{
                                                label: 'Pump Duration',
                                                variant: 'outlined',
                                                InputLabelProps: { shrink: true },
                                                options: durations,
                                                required: true,
                                            }}
                                            uniqueValues={false}
                                            {...{ control, index, field }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={2} sx={{ mt: 1 }}>
                                <Select
                                    key={`break_time_ms.${field.id}`}
                                    name={`${name}.${index}.break_time_ms`}
                                    props={{
                                        label: 'Break',
                                        variant: 'outlined',
                                        InputLabelProps: { shrink: true },
                                        options: durations,
                                        required: true,
                                    }}
                                    uniqueValues={false}
                                    {...{ control, index, field }}
                                    // wrap
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ mt: 1 }}>
                                <Select
                                    key={`wash_time_ms.${field.id}`}
                                    name={`${name}.${index}.wash_time_ms`}
                                    props={{
                                        label: 'Wash Time',
                                        variant: 'outlined',
                                        InputLabelProps: { shrink: true },
                                        options: durations,
                                        required: true,
                                    }}
                                    uniqueValues={false}
                                    {...{ control, index, field }}
                                    // wrap
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ mt: 1 }}>
                                <Select
                                    key={`test_time_ms.${field.id}`}
                                    name={`${name}.${index}.test_time_ms`}
                                    props={{
                                        label: 'Test Time',
                                        variant: 'outlined',
                                        InputLabelProps: { shrink: true },
                                        options: durations,
                                        required: true,
                                    }}
                                    uniqueValues={false}
                                    {...{ control, index, field }}
                                    // wrap
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {fields.length > 1 && index !== 0 && (
                        <Grid item xs={1} sx={RemoveButtonSx}>
                            <Button onClick={() => remove(index)}>
                                <HighlightOffIcon style={{ color: 'red' }} />
                            </Button>
                        </Grid>
                    )}
                </Grid>
            ))}
            {!usePumpSpeed && (
                <Button
                    variant="outlined"
                    onClick={() => append({ maxWeight: 0, duration: 0, break_time_ms: 0, wash_time_ms: 0, test_time_ms: 0 })}
                    disabled={!validateMaxWeight()}
                    // wrap
                >
                    <AddIcon sx={{ marginRight: 1 }} /> {buttonLabel ?? label}
                </Button>
            )}
        </Grid>
    );
}

export default PumpDurations;
